import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { api } from '../api';
import { useApp } from '../contexts/AppContext';

const GCC_LIST = [
  { code: 'KW', ar: 'الكويت',   en: 'Kuwait' },
  { code: 'SA', ar: 'السعودية', en: 'Saudi Arabia' },
  { code: 'AE', ar: 'الإمارات', en: 'UAE' },
  { code: 'BH', ar: 'البحرين',  en: 'Bahrain' },
  { code: 'QA', ar: 'قطر',      en: 'Qatar' },
  { code: 'OM', ar: 'عمان',     en: 'Oman' },
  { code: 'OTHER', ar: 'أخرى',  en: 'Other' },
];

export default function CheckoutPage() {
  const { t, lang, cart, cartTotal, formatPrice, clearCart, countryCode, countries } = useApp();
  const navigate = useNavigate();
  const BackIcon = lang === 'ar' ? ArrowLeft : ArrowRight;

  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_country: countryCode || 'KW',
    customer_city: '',
    customer_address: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.customer_name.trim()) e.customer_name = t.required;
    if (!form.customer_phone.trim()) e.customer_phone = t.required;
    if (!form.customer_country) e.customer_country = t.required;
    if (!form.customer_address.trim()) e.customer_address = t.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await api.createOrder({
        ...form,
        sheep_ids: cart.map(s => s.id),
      });
      clearCart();
      navigate(`/order-success/${res.data.order_id}`);
    } catch (err) {
      setErrors({ submit: err.response?.data?.detail || (lang === 'ar' ? 'حدث خطأ، حاول مجدداً' : 'An error occurred, please try again') });
    }
    setLoading(false);
  };

  const field = (key, label, type = 'text', placeholder = '') => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '12px 14px', borderRadius: 12, fontSize: 14,
          border: `1.5px solid ${errors[key] ? '#ef4444' : '#e5e7eb'}`,
          outline: 'none', fontFamily: 'inherit', direction: 'inherit', boxSizing: 'border-box',
        }}
      />
      {errors[key] && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{errors[key]}</div>}
    </div>
  );

  return (
    <div className="page-pb page-center">
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#8B1A1A', fontWeight: 600 }}>
          <BackIcon size={18} />{lang === 'ar' ? 'رجوع' : 'Back'}
        </button>
        <span style={{ fontSize: 18, fontWeight: 700 }}>{t.orderDetails}</span>
      </div>

      {/* Order summary */}
      <div style={{ margin: '0 16px 16px', background: '#fff', borderRadius: 16, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ fontWeight: 700, marginBottom: 10, color: '#374151' }}>{lang === 'ar' ? 'ملخص الطلب' : 'Order Summary'}</div>
        {cart.map(s => (
          <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
            <span>{lang === 'ar' ? s.breed_ar : s.breed_en || s.given_name}</span>
            <span style={{ fontWeight: 600, color: '#166534' }}>{formatPrice(s.online_price)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 16, marginTop: 10 }}>
          <span>{t.total}</span>
          <span style={{ color: '#166534' }}>{formatPrice(cartTotal)}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ padding: '0 16px' }}>
        {field('customer_name', lang === 'ar' ? 'الاسم الكامل' : 'Full Name', 'text', lang === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name')}
        {field('customer_phone', lang === 'ar' ? 'رقم الهاتف' : 'Phone Number', 'tel', '+965 XXXXXXXX')}

        {/* Country select */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            {lang === 'ar' ? 'الدولة' : 'Country'}
          </label>
          <select
            value={form.customer_country}
            onChange={e => setForm(f => ({ ...f, customer_country: e.target.value }))}
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 12, fontSize: 14,
              border: `1.5px solid ${errors.customer_country ? '#ef4444' : '#e5e7eb'}`,
              outline: 'none', fontFamily: 'inherit', background: '#fff', boxSizing: 'border-box',
            }}
          >
            {GCC_LIST.map(c => (
              <option key={c.code} value={c.code}>{lang === 'ar' ? c.ar : c.en}</option>
            ))}
          </select>
          {errors.customer_country && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{errors.customer_country}</div>}

          {/* Show buying disabled warning */}
          {form.customer_country && countries[form.customer_country] && !countries[form.customer_country].buying_enabled && (
            <div style={{ marginTop: 8, background: '#fff8e1', border: '1px solid #fbbf24', borderRadius: 10, padding: '8px 12px', fontSize: 13, color: '#92400e' }}>
              {t.buyingNotAvailable}
            </div>
          )}
        </div>

        {field('customer_city', lang === 'ar' ? 'المدينة' : 'City', 'text')}
        {field('customer_address', lang === 'ar' ? 'العنوان التفصيلي' : 'Detailed Address', 'text', lang === 'ar' ? 'المنطقة، الشارع، رقم المنزل' : 'Area, Street, Building/House No.')}

        {/* Notes */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            {lang === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (optional)'}
          </label>
          <textarea
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            rows={3}
            style={{
              width: '100%', padding: '12px 14px', borderRadius: 12, fontSize: 14,
              border: '1.5px solid #e5e7eb', outline: 'none', fontFamily: 'inherit',
              resize: 'none', boxSizing: 'border-box', direction: 'inherit',
            }}
          />
        </div>

        {errors.submit && (
          <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 12, padding: '10px 14px', marginBottom: 16, fontSize: 14 }}>
            {errors.submit}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%', background: loading ? '#9ca3af' : '#8B1A1A', color: '#fff',
            border: 'none', borderRadius: 16, padding: '14px 0',
            cursor: loading ? 'not-allowed' : 'pointer', fontSize: 16, fontWeight: 700
          }}
        >
          {loading ? (lang === 'ar' ? 'جاري الإرسال...' : 'Sending...') : t.placeOrder}
        </button>
      </form>
    </div>
  );
}
